/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect } from 'react';
import { Message } from 'primereact/message';
import { Button } from 'sharedUi/Button';
import { useBreadcrumbTitle } from 'sharedUi/BreadcrumbContext';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { evalCasesService } from '../../services/eval-cases.service';
import type { EvalCase } from '../../types';
import { extractErrorMessage } from '../../utils/error-handler';
import DeleteConfirmDialog from '../../components/dialogs/delete-confirm-dialog';
import EvalCaseDetails, { EvalCaseChips } from './eval-case-details';
import { PageSkeleton } from '../../components/common/panel';
import ScreenHeader from '../../components/common/screen-header';
import { useCanUpdate, useCanDelete } from '../../hooks/usePermissions';
import { JOB_NAMES } from '../../constants/jobNames';

const EvalCaseView = () => {
  const { id, isReady, navigateToEdit, navigateToList } = useSecureNavigation({ entity: 'eval-case', basePath: '/eval-cases' });
  const canUpdate = useCanUpdate(JOB_NAMES.EVAL_CASE);
  const canDelete = useCanDelete(JOB_NAMES.EVAL_CASE);

  const [data, setData] = useState<EvalCase | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useBreadcrumbTitle(data?.name);

  useEffect(() => {
    if (!isReady || !id) return;
    (async () => {
      try {
        setLoading(true);
        setData(await evalCasesService.getById(id));
      } catch (err: any) {
        setError(extractErrorMessage(err, 'Failed to fetch eval case details'));
      } finally {
        setLoading(false);
      }
    })();
  }, [isReady, id]);

  const handleDeleteConfirm = async () => {
    if (!id) return;
    try {
      await evalCasesService.softDelete(id);
      setDeleteDialogOpen(false);
      navigateToList();
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to delete eval case'));
      setDeleteDialogOpen(false);
    }
  };

  if (loading) return <PageSkeleton panels={1} />;

  if (error || !data) {
    return (
      <div className="p-4">
        <Message severity="error" text={error || 'Eval case not found'} className="mb-4 w-full" />
        <Button label="Back to Library" icon="pi pi-arrow-left" severity="secondary" outlined onClick={() => navigateToList()} />
      </div>
    );
  }

  return (
    <div className="p-4" data-testid="SCR-EvalCase-View">
      <ScreenHeader
        icon="pi pi-check-square"
        onBack={() => navigateToList()}
        title={data.name}
        right={
          <>
            {data.caseCode && <span className="font-mono text-xs text-ink-muted">{data.caseCode}</span>}
            <EvalCaseChips data={data} />
            {!(data as any).isDelete && (
              <>
                {canUpdate && <span className="ui-press inline-flex"><Button label="Edit" icon="pi pi-pencil" onClick={() => id && navigateToEdit(id)} size="small" /></span>}
                {canDelete && <span className="ui-press inline-flex"><Button label="Delete" icon="pi pi-trash" severity="danger" onClick={() => setDeleteDialogOpen(true)} size="small" /></span>}
              </>
            )}
          </>
        }
      />

      <EvalCaseDetails data={data} />

      <DeleteConfirmDialog
        visible={deleteDialogOpen}
        onCancel={() => setDeleteDialogOpen(false)}
        onDelete={handleDeleteConfirm}
        message="Delete this eval case? It cannot be deleted while a suite still uses it."
        testId="EvalCaseView.Dialog.Delete"
      />
    </div>
  );
};

export default EvalCaseView;
